import { useRef, useState, type PointerEvent } from 'react';
import { badgeLines, type BadgeMe } from '../accountBadge.ts';

/**
 * Who is signed in, beside Sign out (19 October 2026: the browser had moved Procura to another account and nothing
 * on screen said so). "Firstname Lastname", and under it the primary role's display name, smaller; below 768 px the
 * name alone. The name line shows the username while a mouse is over it, while it has keyboard focus, or after a tap,
 * and changes back on leave, blur or a second tap. Everything is always in the accessible name and the hover text.
 *
 * The badge never changes width when the line swaps (Arthur, 19 October), so nothing beside it in the header moves:
 * the name and the username sit in the same grid cell, the one not shown kept in place but invisible, so the cell is
 * always as wide as the longer of the two. Past the badge's maximum width either is cut with an ellipsis.
 */
export function AccountBadge({ me }: { me: BadgeMe }) {
  const [hover, setHover] = useState(false);
  const [keyFocus, setKeyFocus] = useState(false);
  const [tapped, setTapped] = useState(false);
  const lines = badgeLines(me);
  const asked = hover || keyFocus || tapped;

  const enter = (e: PointerEvent) => { if (e.pointerType === 'mouse') setHover(true); };
  // A mouse leaving ends what it showed. A finger lifting also fires a leave, BEFORE its click: that must not undo the tap.
  const leave = (e: PointerEvent) => { if (e.pointerType === 'mouse') { setHover(false); setTapped(false); } };
  // Keyboard focus only: a tap or a click focuses the button too, and the tap itself decides there. A pointer pressed
  // just before the focus says it was not the keyboard (:focus-visible cannot be trusted on touch).
  const pressed = useRef(false);
  const down = () => { pressed.current = true; };
  const focus = () => { setKeyFocus(!pressed.current); };
  const blur = () => { pressed.current = false; setKeyFocus(false); setTapped(false); };

  const line = 'col-start-1 row-start-1 min-w-0 truncate';
  return (
    <button type="button" aria-label={lines.label} title={lines.tooltip}
            onPointerEnter={enter} onPointerLeave={leave} onPointerDown={down} onFocus={focus} onBlur={blur}
            onClick={() => setTapped((t) => !t)}
            className="flex min-h-[44px] min-w-0 max-w-[18rem] shrink flex-col items-end justify-center rounded-lg px-2 text-right leading-tight text-on-brand">
      <span className="grid max-w-full grid-cols-[minmax(0,max-content)] text-right text-sm font-semibold">
        <span data-line="name" className={`${line} ${asked ? 'invisible' : ''}`} aria-hidden={asked}>{lines.name}</span>
        <span data-line="username" className={`${line} ${asked ? '' : 'invisible'}`} aria-hidden={!asked}>{lines.handle}</span>
      </span>
      {lines.title && <span data-line="title" className="hidden max-w-full truncate text-xs text-on-brand-muted md:block">{lines.title}</span>}
    </button>
  );
}
